/* eslint-disable max-lines */
import React from 'react';
import { Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import InputMask from 'react-input-mask';
import NumberFormat from 'react-number-format';

interface NewHireStepOneProps {
  handleSubmitForm: (e: React.FormEvent<HTMLFormElement>) => void;
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  isEmailExists: boolean;
  confirmEmail: string;
  setConfirmEmail: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  groupId: string;
  setGroupId: (value: string) => void;
  groupsList: any[];
  classification: string;
  setClassification: (value: string) => void;
  paymentFrequency: string | undefined;
  setPaymentFrequency: (value: string) => void;
  amount: number | string | undefined;
  setAmount: (value: any) => void;
  policy: string | undefined;
  setPolicy: (value: string) => void;
  ptoPolices: any[];
}

const NewHireStepOne: React.FC<NewHireStepOneProps> = ({
  handleSubmitForm,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  isEmailExists,
  confirmEmail,
  setConfirmEmail,
  phone,
  setPhone,
  groupId,
  setGroupId,
  groupsList,
  classification,
  setClassification,
  paymentFrequency,
  setPaymentFrequency,
  amount,
  setAmount,
  policy,
  setPolicy,
  ptoPolices,
}) => {
  const handleAmountChange = (values: any) => {
    const { floatValue } = values;
    setAmount(floatValue === undefined ? '' : floatValue);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    return false;
  };

  return (
    <Form onSubmit={handleSubmitForm}>
      <Row className="d-flex align-items-center">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="firstNameInput">
            First Name:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="firstNameInput"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            placeholder="Type the first name"
          />
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="lastNameInput">
            Last Name:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="lastNameInput"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            placeholder="Type the last name"
          />
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="emailInput">
            Email Address:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="emailInput"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="off"
            placeholder="example@domain.com"
          />
          {isEmailExists && (
            <label className="text-sm text-danger mt-1 d-block">This email already exists for an active user.</label>
          )}
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="confirmEmailInput">
            Confirm Email:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="confirmEmailInput"
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            required
            autoComplete="off"
            onPaste={handlePaste}
            placeholder="Confirm the email"
          />
          {email && confirmEmail && email !== confirmEmail && (
            <label className="text-sm text-danger mt-1 d-block">Emails do not match.</label>
          )}
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="phoneInput">
            Phone Number:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="phoneInput"
            type="text"
            mask="(999) 999-9999"
            tag={InputMask}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            placeholder="(XXX) XXX-XXXX"
          />
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="permissionGroupSelect">
            Permission Group:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="permissionGroupSelect"
            type="select"
            onChange={(e) => setGroupId(e.target.value)}
            value={groupId}
            required
          >
            <option value="">Select a group...</option>
            {groupsList?.map((row: any, index: number) => {
              return (
                <option value={row.id} key={`permission-group-${index}`}>
                  {row.name}
                </option>
              );
            })}
          </Input>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label">Classification:</Label>
        </Col>
        <Col lg="9">
          <div className="d-flex flex-wrap">
            <FormGroup check inline className="mr-3">
              <Label check>
                <Input
                  className="form-check-input"
                  type="radio"
                  name="classificationRadio"
                  id="classification-ft"
                  value={'FT'}
                  checked={classification === 'FT'}
                  onChange={(e) => setClassification(e.target.value)}
                  required
                />
                Full Time
              </Label>
            </FormGroup>
            <FormGroup check inline className="mr-3">
              <Label check>
                <Input
                  className="form-check-input"
                  type="radio"
                  name="classificationRadio"
                  id="classification-pt"
                  value={'PT'}
                  checked={classification === 'PT'}
                  onChange={(e) => setClassification(e.target.value)}
                  required
                />
                Part Time
              </Label>
            </FormGroup>
            <FormGroup check inline className="mr-3">
              <Label check>
                <Input
                  className="form-check-input"
                  type="radio"
                  name="classificationRadio"
                  id="classification-sub"
                  value={'Sub'}
                  checked={classification === 'Sub'}
                  onChange={(e) => setClassification(e.target.value)}
                  required
                />
                Sub
              </Label>
            </FormGroup>
          </div>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="paymentFrequencySelect">
            Payment Frequency:
          </Label>
        </Col>
        <Col lg="9">
          <Input
            id="paymentFrequencySelect"
            type="select"
            value={paymentFrequency}
            onChange={(e) => setPaymentFrequency(e.target.value)}
            required
            disabled={!classification}
          >
            <option value="">Select frequency...</option>
            <option value={'hourly'}>Hourly</option>
            {(classification === 'FT' || classification === '') && <option value={'annually'}>Annually</option>}
          </Input>
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="amountInput">
            Amount:
          </Label>
        </Col>
        <Col lg="9">
          <NumberFormat
            id="amountInput"
            className="form-control"
            thousandsGroupStyle="thousand"
            prefix="$ "
            decimalSeparator="."
            displayType="input"
            type="text"
            thousandSeparator={true}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale={true}
            value={amount === '' ? '' : Number(amount)}
            onValueChange={handleAmountChange}
            placeholder="$ 0.00"
            required
            disabled={!paymentFrequency}
          />
        </Col>
      </Row>

      <Row className="d-flex align-items-center mt-4">
        <Col lg="3">
          <Label className="form-control-label" htmlFor="ptoPolicySelect">
            PTO Policy:
          </Label>
        </Col>
        <Col lg="9">
          <Input id="ptoPolicySelect" type="select" value={policy} onChange={(e) => setPolicy(e.target.value)} required>
            <option value="">Select a policy...</option>
            {ptoPolices?.map((value) => (
              <option key={value.id} value={value.id}>
                {value.name}
              </option>
            ))}
          </Input>
        </Col>
      </Row>

      <hr />

      <div className="d-flex justify-content-end mt-4">
        <div>
          <Button
            color="warning"
            type="submit"
            disabled={isEmailExists || (!!email && !!confirmEmail && email !== confirmEmail)}
          >
            Continue
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default NewHireStepOne;
